import React, { useEffect, useState } from "react";
import { environment } from "environment/environment";
import useNiftyWebsocket from "service/websocket/NiftyWebsocketApi";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../common/AuthContext";
import { getSectorStrategyPerformance } from "service/api/SectorStrategyApi";

const displayValue = (value) => value ?? "--";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "--";
  const absoluteValue = Math.abs(numericValue).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${numericValue < 0 ? "-₹" : "₹"}${absoluteValue}`;
};

const changeTone = (value) => {
  const numericValue = Number.parseFloat(value);

  if (Number.isNaN(numericValue) || numericValue === 0) return "neutral";
  return numericValue > 0 ? "positive" : "negative";
};

const MarketCard = ({ market }) => {
  const navigate = useNavigate();
  const changeClass = changeTone(market.data?.ch);
  const percentageClass = changeTone(market.data?.chp);
  const indexName = (market.data?.short_name || market.fallbackName).replace(/-INDEX$/i, "");

  const openOptionChain = () => navigate("/nse-option-chain");
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openOptionChain();
    }
  };

  return (
    <article
      className={`market-card market-card--${market.tone} market-card--percentage-${percentageClass} market-card--interactive`}
      role="link"
      tabIndex={0}
      aria-label={`Open ${indexName} option chain`}
      onClick={openOptionChain}
      onKeyDown={handleKeyDown}
    >
      <div className="market-card__topline">
        <div className="market-card__identity">
          <span className="market-card__icon" aria-hidden="true">{market.shortLabel}</span>
          <div>
            <span className="market-card__eyebrow">{market.exchange}</span>
            <h3>{indexName}</h3>
          </div>
        </div>
        <div className="market-card__header-values">
          <div className="market-card__spot-price">
            <strong>{displayValue(market.data?.lp)}</strong>
          </div>
          <span className={`market-card__percentage market-card__percentage--${percentageClass}`}>
            {displayValue(market.data?.chp)}%
          </span>
          <span className={`market-card__change market-card__change--${changeClass}`}>
            {displayValue(market.data?.ch)}
            {market.data?.ch != null && " points"}
          </span>
        </div>
      </div>

      <dl className="market-card__details">
        <div><dt>Prev close</dt><dd>{displayValue(market.data?.prev_close_price)}</dd></div>
        <div><dt>Open price</dt><dd>{displayValue(market.data?.open_price)}</dd></div>
        <div><dt>Close price</dt><dd>{displayValue(market.data?.close_price ?? market.data?.lp)}</dd></div>
        <div><dt>Day high</dt><dd>{displayValue(market.data?.high_price)}</dd></div>
        <div><dt>Day low</dt><dd>{displayValue(market.data?.low_price)}</dd></div>
      </dl>
    </article>
  );
};

const DetailRow = ({ label, value, valueClassName = "" }) => (
  <div className="dashboard-detail-row">
    <dt>{label}</dt>
    <dd className={valueClassName}>{displayValue(value)}</dd>
  </div>
);

const Dashboard = () => {
  const { options } = useNiftyWebsocket(environment.niftyWebsocketUrl);
  const [niftyIndexLiveData, setNiftyIndexLiveData] = useState(null);
  const { user, userPlan, isLoading, isPlanLoading } = useAuth();
  const navigate = useNavigate();
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(true);
  const [performanceError, setPerformanceError] = useState(false);

  // ✅ Redirect to login if not logged in
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  // ✅ Handle live data from websocket
  useEffect(() => {
    if (!options) return;

    setNiftyIndexLiveData(options["NSE:NIFTY"]?.indexLiveDataModel || null);
  }, [options]);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.userId;
    const accessToken = user?.accessToken;

    if (!userId || !accessToken) {
      setPerformance(null);
      setPerformanceLoading(false);
      setPerformanceError(false);
      return undefined;
    }

    setPerformanceLoading(true);
    setPerformanceError(false);

    const loadPerformance = async () => {
      try {
        const summary = await getSectorStrategyPerformance(userId, accessToken);
        if (!cancelled) setPerformance(summary && typeof summary === "object" ? summary : null);
      } catch (error) {
        if (!cancelled) {
          console.error("Unable to load current-month performance:", error);
          setPerformance(null);
          setPerformanceError(true);
        }
      } finally {
        if (!cancelled) setPerformanceLoading(false);
      }
    };

    loadPerformance();
    const refreshTimer = window.setInterval(loadPerformance, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
    };
  }, [user?.accessToken, user?.userId]);

  if (isLoading) return <div className="dashboard-loading">Loading your dashboard…</div>;
  if (!user) return null;

  const firstName = user.userName?.trim()?.split(" ")[0] || "there";
  const hasActivePlan = Boolean(userPlan);
  const accessLabel = isPlanLoading
    ? "Checking access"
    : hasActivePlan
      ? "Access active"
      : "Subscription needed";

  return (
    <main className="dashboard-container" aria-labelledby="dashboard-title">
      <div className="dashboard-shell">
        <section className="dashboard-hero" aria-labelledby="dashboard-title">
          <div className="dashboard-hero__content">
            <span className="dashboard-eyebrow">MARKET INTELLIGENCE WORKSPACE</span>
            <h1 id="dashboard-title">Welcome back, {firstName}</h1>
            <p>Monitor live indices and keep your market tools within reach from one focused workspace.</p>
            <div className="dashboard-hero__actions">
              <button type="button" className="dashboard-primary-action" onClick={() => navigate("/subscribe")}>
                {hasActivePlan ? "Manage membership" : "View membership plans"}
              </button>
              <span className="dashboard-hero__hint">One place for your daily market view</span>
            </div>
          </div>

          <div className={`dashboard-hero__status ${hasActivePlan ? "is-active" : "is-expired"}`}>
            <span className="dashboard-hero__status-label">
              <span className="dashboard-status-dot" aria-hidden="true" />
              {accessLabel}
            </span>
            <strong>
              {isPlanLoading
                ? "Please wait"
                : hasActivePlan
                  ? `${userPlan.daysLeft ?? "--"} days remaining`
                  : "Renew to unlock tools"}
            </strong>
            <span>
              {hasActivePlan
                ? `${userPlan.planName || "Membership"} · ${userPlan.planValidity ?? "--"} days`
                : "Your dashboard remains available while access is renewed."}
            </span>
          </div>
        </section>

        <section className="dashboard-section" aria-labelledby="overview-title">
          <div className="dashboard-section__heading">
            <div>
              <span className="dashboard-eyebrow">YOUR ACCOUNT</span>
              <h2 id="overview-title">Overview</h2>
            </div>
            <span className="dashboard-section__caption">A quick view of your account and membership</span>
          </div>

          <div className="dashboard-overview-grid">
            <article className="dashboard-card dashboard-card--account">
              <div className="dashboard-card__heading">
                <div className="dashboard-card__title-wrap">
                  <span className="dashboard-card__icon dashboard-card__icon--blue" aria-hidden="true"><i className="fa fa-user" /></span>
                  <div>
                    <span className="dashboard-card__kicker">ACCOUNT</span>
                    <h3>Profile details</h3>
                  </div>
                </div>
              </div>
              <dl className="dashboard-detail-list">
                <DetailRow label="User ID" value={user.userId} />
                <DetailRow label="Name" value={user.userName} />
                <DetailRow label="Mobile" value={user.mobileNumber} />
                <DetailRow label="Email" value={user.email} />
                <DetailRow label="Role" value={user.role || user.post || "Admin"} />
              </dl>
            </article>

            <article className={`dashboard-card dashboard-card--membership ${hasActivePlan ? "is-active" : "is-expired"}`}>
              <div className="dashboard-card__heading">
                <div className="dashboard-card__title-wrap">
                  <span className="dashboard-card__icon dashboard-card__icon--green" aria-hidden="true"><i className="fa fa-shield" /></span>
                  <div>
                    <span className="dashboard-card__kicker">MEMBERSHIP</span>
                    <h3>Plan &amp; validity</h3>
                  </div>
                </div>
                <span className={`dashboard-status-pill ${hasActivePlan ? "is-active" : "is-expired"}`}>
                  <span className="dashboard-status-dot" aria-hidden="true" />
                  {isPlanLoading ? "Checking" : hasActivePlan ? "Active" : "Expired"}
                </span>
              </div>

              {!isPlanLoading && !hasActivePlan && (
                <p className="dashboard-card__notice">Your previous plan has ended. Renew below to restore access to paid tools.</p>
              )}

              <dl className="dashboard-detail-list">
                <DetailRow label="Plan" value={userPlan?.planName || "No active plan"} />
                <DetailRow label="Amount" value={userPlan ? `₹${userPlan.planAmount ?? "--"}` : "--"} />
                <DetailRow label="Validity" value={userPlan ? `${userPlan.planValidity ?? "--"} days` : "--"} />
                <DetailRow
                  label="Days left"
                  value={userPlan?.daysLeft}
                  valueClassName={hasActivePlan ? "dashboard-value--green" : "dashboard-value--muted"}
                />
                <DetailRow label="Ends" value={userPlan?.subscribeDateEnd} />
              </dl>
            </article>

            <article className="dashboard-card dashboard-card--performance">
              <div className="dashboard-card__heading">
                <div className="dashboard-card__title-wrap">
                  <span className="dashboard-card__icon dashboard-card__icon--purple" aria-hidden="true"><i className="fa fa-line-chart" /></span>
                  <div>
                    <span className="dashboard-card__kicker">SNAPSHOT</span>
                    <h3>Performance</h3>
                  </div>
                </div>
              </div>
              <dl className="dashboard-detail-list performance-detail-list">
                <DetailRow
                  label="Total trades"
                  value={performanceLoading ? "…" : displayValue(performance?.totalTrades)}
                />
                <DetailRow
                  label="Profit"
                  value={performanceLoading ? "…" : formatMoney(performance?.grossProfit)}
                  valueClassName="dashboard-value--green"
                />
                <DetailRow
                  label="Loss"
                  value={performanceLoading ? "…" : formatMoney(performance?.grossLoss)}
                  valueClassName="dashboard-value--red"
                />
                <DetailRow
                  label="Final P&amp;L"
                  value={performanceLoading ? "…" : formatMoney(performance?.finalPnl)}
                  valueClassName={Number(performance?.finalPnl) < 0 ? "dashboard-value--red" : "dashboard-value--green"}
                />
                <DetailRow
                  label="Win rate"
                  value={performanceLoading ? "…" : performance?.winRate == null ? "--" : `${performance.winRate}%`}
                />
              </dl>
              <small className="performance-period" role="status">
                {performanceLoading
                  ? "Loading current-month performance…"
                  : performanceError
                    ? "Current-month performance is unavailable."
                    : `Current month: ${performance?.periodStart ?? "--"} to ${performance?.periodEnd ?? "--"}`}
              </small>
            </article>
          </div>
        </section>

        <section className="dashboard-section dashboard-section--markets" aria-labelledby="markets-title">
          <div className="dashboard-section__heading">
            <div>
              <span className="dashboard-eyebrow">LIVE MARKET DATA</span>
              <h2 id="markets-title">Market snapshot</h2>
            </div>
            <div className="dashboard-live-indicator" aria-live="polite">
              <span className="dashboard-status-dot" aria-hidden="true" />
              Streaming indices
            </div>
          </div>

          <div className="market-grid">
            <MarketCard
              market={{
                data: niftyIndexLiveData,
                exchange: "NSE INDEX",
                fallbackName: "NIFTY 50",
                shortLabel: "N",
                tone: "blue",
              }}
            />
          </div>
        </section>
      </div>
    </main>
  );

};

export default Dashboard;
